import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { Trash2, Plus, Phone, Mail, MessageSquare, Video, MapPin, FileEdit, ListTodo, Briefcase, BarChart2, Coffee, CalendarDays } from "lucide-react";

const TIPO_OPTIONS = [
    { v: "livre", l: "Anotação livre", icon: FileEdit, color: "#64748B" },
    { v: "ligacao", l: "Ligação", icon: Phone, color: "#0EA5E9" },
    { v: "email", l: "E-mail", icon: Mail, color: "#6366F1" },
    { v: "whatsapp", l: "WhatsApp", icon: MessageSquare, color: "#22C55E" },
    { v: "reuniao_externa", l: "Chamada de vídeo", icon: Video, color: "#8B5CF6" },
    { v: "visita", l: "Visita presencial", icon: MapPin, color: "#F59E0B" },
    { v: "outro", l: "Outro", icon: FileEdit, color: "#64748B" },
];

const TIPO_META = {
    livre: { l: "Anotação", icon: FileEdit, color: "#64748B" },
    ligacao: { l: "Ligação", icon: Phone, color: "#0EA5E9" },
    email: { l: "E-mail", icon: Mail, color: "#6366F1" },
    whatsapp: { l: "WhatsApp", icon: MessageSquare, color: "#22C55E" },
    reuniao_externa: { l: "Chamada de vídeo", icon: Video, color: "#8B5CF6" },
    visita: { l: "Visita", icon: MapPin, color: "#F59E0B" },
    outro: { l: "Outro", icon: FileEdit, color: "#64748B" },
    tarefa_concluida: { l: "Tarefa concluída", icon: ListTodo, color: "#10B981" },
    reuniao: { l: "Reunião CS", icon: Coffee, color: "#0F2044" },
    uso: { l: "Uso registrado", icon: BarChart2, color: "#3B82F6" },
    acao_criada: { l: "Ação criada", icon: Briefcase, color: "#F59E0B" },
    acao_concluida: { l: "Ação concluída", icon: Briefcase, color: "#10B981" },
};

export default function HistoricoTab({ contract }) {
    const [events, setEvents] = useState([]);
    const [filter, setFilter] = useState("all");
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        data: new Date().toISOString().slice(0, 10),
        tipo: "livre", titulo: "", descricao: "",
    });

    async function refresh() {
        const r = await api.get(`/contracts/${contract.id}/timeline`);
        setEvents(r.data);
    }

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [contract.id]);

    async function submit(e) {
        e.preventDefault();
        if (!form.descricao.trim()) return;
        setSaving(true);
        try {
            await api.post(`/contracts/${contract.id}/notes`, form);
            await refresh();
            setForm({ data: new Date().toISOString().slice(0, 10), tipo: "livre", titulo: "", descricao: "" });
        } finally { setSaving(false); }
    }

    async function removeNote(refId) {
        if (!window.confirm("Excluir esta anotação?")) return;
        await api.delete(`/contracts/${contract.id}/notes/${refId}`);
        await refresh();
    }

    const filtered = filter === "all" ? events : events.filter(e => {
        if (filter === "interacao") return ["livre", "ligacao", "email", "whatsapp", "reuniao_externa", "visita", "outro"].includes(e.tipo);
        if (filter === "operacional") return ["tarefa_concluida", "uso", "acao_criada", "acao_concluida"].includes(e.tipo);
        if (filter === "reuniao") return e.tipo === "reuniao";
        return true;
    });

    // group by month
    const groups = {};
    filtered.forEach(e => {
        const key = e.data?.slice(0, 7) || "—";
        (groups[key] = groups[key] || []).push(e);
    });
    const groupKeys = Object.keys(groups).sort().reverse();

    return (
        <div className="grid grid-cols-3 gap-6" data-testid="historico-tab">
            <div className="col-span-2 space-y-4">
                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="historico-filters">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display font-semibold text-base text-[#0F2044]">Linha do tempo do cliente</h3>
                        <div className="flex gap-1.5 flex-wrap">
                            {[
                                ["all", "Tudo"], ["interacao", "Interações"], ["reuniao", "Reuniões"], ["operacional", "Operacional"],
                            ].map(([v, l]) => (
                                <button key={v} onClick={() => setFilter(v)} data-testid={`hist-filter-${v}`}
                                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${filter === v ? "bg-[#0F2044] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="text-xs text-slate-500">{filtered.length} evento(s)</div>
                </div>

                {filtered.length === 0 && (
                    <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500">
                        Sem eventos para o filtro selecionado.
                    </div>
                )}

                {groupKeys.map((mk) => (
                    <div key={mk} className="space-y-2">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold pl-1">
                            <CalendarDays className="h-3 w-3" />
                            {monthLabelFromKey(mk)}
                        </div>
                        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
                            {groups[mk].map((e, idx) => {
                                const meta = TIPO_META[e.tipo] || TIPO_META.outro;
                                const Icon = meta.icon;
                                return (
                                    <li key={idx} className="ml-5 relative" data-testid={`timeline-event-${idx}`}>
                                        <span className="absolute -left-[34px] top-1.5 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white" style={{ background: meta.color + "1A" }}>
                                            <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                                        </span>
                                        <div className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: meta.color }}>
                                                        {meta.l} <span className="text-slate-400 ml-1 normal-case tracking-normal font-normal">· {brl(e.data)}</span>
                                                        {e.autor && <span className="text-slate-400 ml-1 normal-case tracking-normal font-normal">· por {e.autor}</span>}
                                                    </div>
                                                    <div className="text-sm font-medium text-[#0F2044] mt-1">{e.titulo}</div>
                                                    {e.descricao && <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{e.descricao}</div>}
                                                    {e.proximos_passos && <div className="text-xs text-slate-500 mt-2"><strong>Próximos passos:</strong> {e.proximos_passos}</div>}
                                                </div>
                                                {e.fonte === "nota" && (
                                                    <button onClick={() => removeNote(e.ref_id)} data-testid={`hist-delete-${e.ref_id}`}
                                                        className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Excluir">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                ))}
            </div>

            <div>
                <div className="bg-white border border-slate-200 rounded-lg p-5 sticky top-6" data-testid="historico-note-form">
                    <h3 className="font-display font-semibold text-base text-[#0F2044] mb-4 flex items-center gap-2">
                        <Plus className="h-4 w-4 text-[#0F2044]" /> Registrar interação
                    </h3>
                    <form onSubmit={submit} className="space-y-3">
                        <Field label="Tipo de interação">
                            <div className="grid grid-cols-2 gap-1.5">
                                {TIPO_OPTIONS.map(opt => {
                                    const Icon = opt.icon;
                                    const active = form.tipo === opt.v;
                                    return (
                                        <button key={opt.v} type="button" onClick={() => setForm({ ...form, tipo: opt.v })}
                                            data-testid={`note-type-${opt.v}`}
                                            className={`text-[11px] px-2 py-1.5 rounded-md border flex items-center gap-1.5 transition-all ${active ? "border-[#0F2044] bg-[#0F2044] text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                                            <Icon className="h-3 w-3" />
                                            {opt.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>
                        <Field label="Data">
                            <input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                                data-testid="note-data-input" className="input" />
                        </Field>
                        <Field label="Título (opcional)">
                            <input type="text" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                placeholder="Ex.: Cobrança da documentação"
                                data-testid="note-titulo-input" className="input" />
                        </Field>
                        <Field label="Descrição *">
                            <textarea rows={5} required value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                                placeholder="O que foi conversado, decidido ou observado..."
                                data-testid="note-desc-input" className="input" />
                        </Field>
                        <button type="submit" disabled={saving} data-testid="note-submit-button"
                            className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2 rounded-md disabled:opacity-60">
                            {saving ? "Registrando…" : "Registrar"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            {children}
        </div>
    );
}

function monthLabelFromKey(key) {
    if (key === "—") return "Sem data";
    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    const [y, m] = key.split("-");
    return `${meses[parseInt(m, 10) - 1]} de ${y}`;
}
